import { Alert, Box, Card, Stack, Typography } from '@mui/material';
import React from 'react';

const paise = (value) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

export const isQuickBookBooking = (booking) => booking?.bookingSource === 'QUICK_BOOK';

export const quickBookPricingSnapshot = (booking) => {
  if (!isQuickBookBooking(booking)) return null;
  const quickBook = booking?.chargeDetails?.quickBook;
  const pricing = quickBook?.pricing;
  if (!quickBook || !pricing) return null;

  const regularGrossPaise = paise(pricing.regularLineTotalPaise);
  const effectiveGrossPaise = paise(pricing.effectiveLineTotalPaise)
    ?? paise(quickBook.finalChargedTotalPaise);
  const serviceGstPaise = paise(pricing.includedServiceGstPaise);
  const payout = quickBook.payoutBreakdown;
  const effectiveBaseFromPayout = paise(payout?.effectiveServiceBaseTotalPaise);
  const effectiveBasePaise = effectiveBaseFromPayout
    ?? (effectiveGrossPaise !== null && serviceGstPaise !== null
      ? effectiveGrossPaise - serviceGstPaise
      : null);
  const savingsPaise = paise(pricing.grossSavingsTotalPaise)
    ?? (regularGrossPaise !== null && effectiveGrossPaise !== null
      ? regularGrossPaise - effectiveGrossPaise
      : 0);
  const providerPayoutPaise = paise(payout?.providerPayoutTotalPaise)
    ?? paise(quickBook.providerPayoutTotalPaise);

  return {
    currency: pricing.currency || quickBook.currency || 'INR',
    regularGrossPaise,
    effectiveGrossPaise,
    serviceGstPaise,
    effectiveBasePaise,
    savingsPaise,
    offerApplied: pricing.offerActive === true
      && regularGrossPaise !== null
      && effectiveGrossPaise !== null
      && regularGrossPaise > effectiveGrossPaise,
    serviceGstRatePercent: Number(quickBook.tax?.serviceGstRatePercent),
    providerPayoutPaise,
    payoutBreakdown: payout || null,
    taxLiabilityMode: quickBook.taxLiabilityMode || payout?.taxLiabilityMode || null,
  };
};

export const quickBookMoney = (value, currency = 'INR') => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) / 100);
};

export const bookingItemTaxRate = (booking, item) => {
  const totalGst = parseFloat(item?.igst || 0)
    + parseFloat(item?.cgst || 0)
    + parseFloat(item?.sgst || 0);
  const denominator = booking?.bookingSource === 'QUICK_BOOK'
    ? parseFloat(item?.price || 0) * parseFloat(item?.quantity || 0)
    : parseFloat(item?.totalAmt);

  return denominator > 0 ? `${((totalGst / denominator) * 100).toFixed(0)}%` : '0%';
};

const QuickBookPricing = ({ booking }) => {
  const snapshot = quickBookPricingSnapshot(booking);
  if (!snapshot) return null;

  return (
    <Card data-testid="quick-book-pricing" variant="outlined" sx={{ m: 1, p: { xs: 1.25, sm: 1.5 }, bgcolor: '#f8fbff' }}>
      <Typography component="h3" sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 1 }}>
        Quick Book price snapshot
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: 'baseline' }}>
        {snapshot.offerApplied && (
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Quick Book Regular Price{' '}
            <Box component="span" data-testid="quick-book-regular-gross" sx={{ textDecoration: 'line-through' }}>
              {quickBookMoney(snapshot.regularGrossPaise, snapshot.currency)}
            </Box>
          </Typography>
        )}
        <Typography data-testid="quick-book-effective-gross" color={snapshot.offerApplied ? 'success.main' : 'text.primary'} sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
          {snapshot.offerApplied ? 'Quick Book Offer Price' : 'Quick Book Regular Price'}: {quickBookMoney(snapshot.effectiveGrossPaise, snapshot.currency)}
        </Typography>
      </Stack>
      {snapshot.offerApplied && snapshot.savingsPaise > 0 && (
        <Alert severity="success" icon={false} sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5, fontSize: '0.72rem' } }}>
          You Save {quickBookMoney(snapshot.savingsPaise, snapshot.currency)}. This is an informational CuDel-funded offer snapshot.
        </Alert>
      )}
    </Card>
  );
};

export default QuickBookPricing;
